import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatDurationSeconds } from '@/lib/format'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface ShiftTimeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Shifts every timestamp in the activity (records, events, laps,
 * sessions, file creation time) by a fixed offset -- e.g. to correct a
 * device clock that was never synced. No range to pick (it applies to
 * the whole activity), so this dialog skips `RangeFields`/the preview
 * hook entirely and just computes `offsetMs` from hours/minutes/seconds
 * + direction.
 */
export function ShiftTimeDialog({ open, onOpenChange }: ShiftTimeDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [hours, setHours] = useState('0')
  const [minutes, setMinutes] = useState('0')
  const [seconds, setSeconds] = useState('0')
  const [submitting, setSubmitting] = useState(false)

  const h = Number(hours)
  const m = Number(minutes)
  const s = Number(seconds)
  const valid = [h, m, s].every((n) => Number.isFinite(n) && n >= 0)
  const magnitudeMs = (h * 3600 + m * 60 + s) * 1000
  const offsetMs = direction === 'forward' ? magnitudeMs : -magnitudeMs
  const isNoOp = valid && magnitudeMs === 0

  if (loaded == null) return null

  const handleConfirm = async () => {
    if (!valid || isNoOp) return
    setSubmitting(true)
    try {
      // `null` means the apply failed (the store already surfaced a
      // toast) -- keep the dialog open rather than closing it as if the
      // shift had actually been applied.
      const warnings = await applyOperationToSelected({ type: 'shiftTime', offsetMs })
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="shift-time-description">
        <DialogTitle>Shift time</DialogTitle>
        <DialogDescription id="shift-time-description">
          Moves every timestamp in the activity forward or backward by a fixed amount -- useful for
          correcting a device clock that recorded the wrong time.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          <div role="radiogroup" aria-label="Direction" className="flex gap-4 text-sm">
            <label className="text-foreground flex items-center gap-2">
              <input
                type="radio"
                name="shift-direction"
                checked={direction === 'forward'}
                onChange={() => setDirection('forward')}
              />
              Forward (later)
            </label>
            <label className="text-foreground flex items-center gap-2">
              <input
                type="radio"
                name="shift-direction"
                checked={direction === 'backward'}
                onChange={() => setDirection('backward')}
              />
              Backward (earlier)
            </label>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <NumberField id="shift-hours" label="Hours" value={hours} onChange={setHours} />
            <NumberField id="shift-minutes" label="Minutes" value={minutes} onChange={setMinutes} />
            <NumberField id="shift-seconds" label="Seconds" value={seconds} onChange={setSeconds} />
          </div>

          {!valid && <p className="text-destructive text-xs">Enter non-negative whole numbers.</p>}
          {valid && (
            <p className="text-muted-foreground text-xs">
              Will shift by {formatDurationSeconds(magnitudeMs / 1000)}{' '}
              {direction === 'forward' ? 'later' : 'earlier'}
              {isNoOp && ' -- enter a non-zero amount to apply a shift'}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid || isNoOp || submitting}
            onClick={() => void handleConfirm()}
          >
            Shift time
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-foreground text-xs font-medium">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
      />
    </div>
  )
}
